import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  TableCell,
  Tooltip,
  Paper,
} from "@mui/material";
import PdfGenerator from "./Pdf/MovementPdf";
import {
  formatAmount,
  formatHistoryAmount,
  getCommaOut,
} from "../../../utils/hooks/dataHandler";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleNotes from "../ScheduleNotes";
import CommonTable from "../../utils/CommonTable";
import {
  CalculatorTextField,
  TextFieldForAmount,
} from "../../utils/TextField/NumericTextField";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import { JustDropDown } from "../../utils/Form/Dropdown";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleContainer from "../utils/ScheduleContainer";
import SectionBox from "../utils/SectionBox";
import {
  APP_LAYOUT_WIDTHS,
  RESPONSIVE_ACTION_THRESHOLDS,
  getAvailableContentWidth,
  getCommonTableDisplayConfig,
  shouldUseLargeCommonTable,
} from "../../../utils/responsiveLayoutSettings";

const SIMPLE_TAX_ADJUSTMENT_COLUMN_CONFIG = {
  baseWidth: 170,
  minWidth: 128,
  shrinkRatio: 0.1,
};

const MOVEMENT_SCHEDULE_TABLE_DISPLAY_CONFIG = {
  default: {
    columns: {
      action: "56px",
      amount: "145px",
      balance: "145px",
      name: "320px",
      reference: "180px",
      source: "64px",
      taxAdjustment: "170px",
    },
    tableMinWidth: "1080px",
  },
  large: {
    columns: {
      action: "52px",
      amount: "135px",
      balance: "135px",
      name: "360px",
      reference: "150px",
      source: "58px",
      taxAdjustment: "145px",
    },
    tableMinWidth: "1035px",
  },
};

const clampNumber = (value, min, max) => Math.min(Math.max(value, min), max);

const getSimpleTaxAdjustmentColumnWidth = (viewportWidth) => {
  const availableWidth =
    typeof viewportWidth === "number"
      ? viewportWidth - APP_LAYOUT_WIDTHS.navigationDrawer
      : getAvailableContentWidth(APP_LAYOUT_WIDTHS.navigationDrawer);
  const widthPressure = Math.max(
    0,
    RESPONSIVE_ACTION_THRESHOLDS.minContentWidthForFullActions - availableWidth
  );
  const adjustedWidth = clampNumber(
    SIMPLE_TAX_ADJUSTMENT_COLUMN_CONFIG.baseWidth -
      widthPressure * SIMPLE_TAX_ADJUSTMENT_COLUMN_CONFIG.shrinkRatio,
    SIMPLE_TAX_ADJUSTMENT_COLUMN_CONFIG.minWidth,
    SIMPLE_TAX_ADJUSTMENT_COLUMN_CONFIG.baseWidth
  );

  return `${Math.round(adjustedWidth)}px`;
};

const getMovementScheduleTableDisplayConfig = (
  large = shouldUseLargeCommonTable(),
  viewportWidth
) => {
  const baseConfig = getCommonTableDisplayConfig(large);
  const movementConfig = large
    ? MOVEMENT_SCHEDULE_TABLE_DISPLAY_CONFIG.large
    : MOVEMENT_SCHEDULE_TABLE_DISPLAY_CONFIG.default;

  return {
    ...baseConfig,
    columns: {
      ...baseConfig.columns,
      ...movementConfig.columns,
      taxAdjustment: getSimpleTaxAdjustmentColumnWidth(viewportWidth),
    },
    tableMinWidth: movementConfig.tableMinWidth,
  };
};

const MovementSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [schName, setSchName] = useState("Provision Movement");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [openingBalance, setOpeningBalance] = useState(1000);
  const [movementBalance, setMovementBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [movementLines, setMovementLines] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [movementTableDisplayConfig, setMovementTableDisplayConfig] = useState(
    () => getMovementScheduleTableDisplayConfig()
  );
  const [openTaxAdjustmentDropdownIndex, setOpenTaxAdjustmentDropdownIndex] =
    useState(null);

  const handleSave = () => {
    schedule.name = schName;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      openingBalance: openingBalance,
      closingBalance: movementBalance,
      movementRows: movementLines
        .filter((line) => line.name !== "Opening Balance")
        .map((line) =>
          Object.fromEntries(
            Object.entries(line).map(([key, obj]) => [key, obj ?? ""])
          )
        ),
      totalAdjustment: closingBalance,
      doc: JSON.stringify({
        openingBalance,
        movementBalance,
        closingBalance,
        movementLines,
        notes,
        reviewNotes,
      }),
    });
  };

  const adjustBalance = (updatedRows) => {
    let amount = updatedRows["amount"];
    let adjustment = updatedRows["taxAdjustment"];
    let adjustedBalance = amount;
    if (adjustment == "Savy Schedule" || adjustment == "Deduct")
      adjustedBalance = -Math.abs(amount);
    else if (adjustment == "Added Back") adjustedBalance = Math.abs(amount);
    else if (!adjustment || adjustment === "" || adjustment === "None")
      adjustedBalance = 0;
    return adjustedBalance;
  };

  const calculateAmountBalance = (rows) => {
    setMovementBalance(
      rows.reduce((sum, row) => {
        const amount = parseFloat(getCommaOut(row.amount)) || 0;
        if (row.amount) return sum + amount;
        else return sum;
      }, 0) || 0
    );

    let newClosingBalance =
      rows.reduce((sum, row) => {
        if (row.amount) {
          const amount = parseFloat(getCommaOut(row.amount)) || 0;
          if (row.taxAdjustment != null && row.taxAdjustment != "") {
            if (row.taxAdjustment === "None") return sum;
            let shouldDeduct =
              row.taxAdjustment === "Savy Schedule" ||
              row.taxAdjustment === "Deduct";
            return shouldDeduct
              ? sum - Math.abs(amount)
              : sum + Math.abs(amount);
          } else return sum;
        }
        return sum;
      }, 0) || 0;
    setClosingBalance(newClosingBalance);

    if (newClosingBalance < 0) schedule.taxAdjustment = "Deduct";
    else schedule.taxAdjustment = "Added Back";
  };

  useEffect(() => {
    const updateMovementTableDisplayConfig = () => {
      setMovementTableDisplayConfig(getMovementScheduleTableDisplayConfig());
    };

    updateMovementTableDisplayConfig();
    window.addEventListener("resize", updateMovementTableDisplayConfig);
    window.visualViewport?.addEventListener(
      "resize",
      updateMovementTableDisplayConfig
    );

    return () => {
      window.removeEventListener("resize", updateMovementTableDisplayConfig);
      window.visualViewport?.removeEventListener(
        "resize",
        updateMovementTableDisplayConfig
      );
    };
  }, []);

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setSourceData(scheduleDetails.sourceLines);
      let lines = [
        {
          name: "Opening Balance",
          amount: formatAmount(scheduleDetails.openingBalance),
          taxAdjustment: null,
          reference: null,
          balance: null,
          permitToDelete: false,
        },
        ...scheduleDetails.movementLines,
      ];

      //calculation
      lines.map((line) => {
        if (line["name"] != "Opening Balance")
          line["balance"] = adjustBalance(line);

        if (!line.historyAmount || line.historyAmount.length <= 0) {
          line.historyAmount = [line.amount];
        }
        return line;
      });

      calculateAmountBalance(lines);

      //init
      setMovementLines(lines);
      setOpeningBalance(scheduleDetails.openingBalance);
    }
  }, []);

  const handleInputChange = (value, { index, field, isOpeningBalance }) => {
    const updatedRows = [...movementLines];

    if (field == "amount") {
      if (!isOpeningBalance) {
        updatedRows[index]["historyAmount"] = value;
        updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);
      } else {
        updatedRows[index][field] = getCommaOut(value);
      }
    } else updatedRows[index][field] = value;

    if (field == "amount" || field == "taxAdjustment") {
      if (!isOpeningBalance)
        updatedRows[index]["balance"] = adjustBalance(updatedRows[index]);

      calculateAmountBalance(updatedRows);
    }
    setMovementLines(updatedRows);

    if (index === 0) setOpeningBalance(getCommaOut(value));
  };

  const addRow = (row = null) => {
    const newRow = {
      name: "",
      amount: 0,
      historyAmount: [],
      taxAdjustment: "",
      reference: "",
      balance: 0,
      permitToDelete: true,
      source: {
        sch_no: "",
      },
    };
    if (row && row !== null) {
      newRow.name = row.description;
      newRow.amount = row.amount;
      newRow.historyAmount = [row.amount];
      newRow.source.sch_no = row.reference;
    }

    setMovementLines((prevRows) => [
      ...prevRows,
      {
        ...newRow,
      },
    ]);
  };

  const removeRow = (index) => {
    let updatedRows = movementLines.filter((_, i) => i !== index);
    updatedRows.map((line) => {
      if (!line.permitToDelete) line["balance"] = adjustBalance(line);
    });
    setMovementLines(updatedRows);
    calculateAmountBalance(updatedRows);
  };

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          handleSave,
          deleteSchedule,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          openingBalance={openingBalance}
          movementBalance={movementBalance}
          closingBalance={closingBalance}
          movementLines={movementLines}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={closingBalance > 0 ? "Added Back" : "Deduct"}
        allowedToEditSchName
        isSourceApplicable
        sourceData={sourceData}
        onImport={(row) => addRow(row)}
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            <SectionBox
              title='Movement Schedule'
              onAdd={addRow}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={movementLines}
                tableDisplayConfig={movementTableDisplayConfig}
                columns={[
                  {
                    key: "name",
                    label: "Description",
                    headerAlign: "left",
                    render: (row, index) => (
                      <>
                        {row.permitToDelete ? (
                          <TextFieldForName
                            value={row.name}
                            onChange={handleInputChange}
                            reference={{
                              index: index,
                              field: "name",
                              isOpeningBalance: !row.permitToDelete,
                            }}
                            canEdit={canEditSchedule}
                          />
                        ) : (
                          <TextFieldForViewOnly
                            value={row.name}
                            sx={{ textAlign: "left" }}
                          />
                        )}
                      </>
                    ),
                  },
                  {
                    key: "taxAdjustment",
                    label: "Tax Adjustment",
                    headerAlign: "center",
                    colAlign: "center",
                    render: (row, index) => {
                      const taxAdjustmentLabel = row.taxAdjustment || "None";
                      const isDropdownOpen =
                        openTaxAdjustmentDropdownIndex === index;

                      return (
                        <>
                          {row.permitToDelete ? (
                            <Tooltip
                              title={taxAdjustmentLabel}
                              disableHoverListener={isDropdownOpen}
                              placement='top'
                            >
                              <Box sx={{ minWidth: 0, width: "100%" }}>
                                <JustDropDown
                                  label=''
                                  value={row.taxAdjustment}
                                  options={["None", "Added Back", "Deduct"]}
                                  onChange={(e) =>
                                    handleInputChange(e.target.value, {
                                      index: index,
                                      field: "taxAdjustment",
                                      isOpeningBalance: !row.permitToDelete,
                                    })
                                  }
                                  onOpen={() =>
                                    setOpenTaxAdjustmentDropdownIndex(index)
                                  }
                                  onClose={() =>
                                    setOpenTaxAdjustmentDropdownIndex(null)
                                  }
                                  maxWidth='100%'
                                  size='small'
                                  sx={{
                                    width: "100%",
                                    "& .MuiSelect-select": {
                                      fontWeight: 400,
                                      minHeight: 18,
                                      overflow: "hidden",
                                      pr: "26px !important",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                    "& .MuiSelect-icon": {
                                      fontSize: "1rem",
                                      right: 6,
                                    },
                                  }}
                                  canEdit={canEditSchedule}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <></>
                          )}
                        </>
                      );
                    },
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, index) => (
                      <>
                        {row.permitToDelete ? (
                          <Tooltip
                            title={
                              row?.historyAmount &&
                              row.historyAmount.length > 0 &&
                              formatHistoryAmount(row.historyAmount)
                            }
                            placement='top'
                          >
                            <Box display='inline-block' sx={{ width: "100%" }}>
                              <CalculatorTextField
                                value={row?.historyAmount || []}
                                onChange={handleInputChange}
                                reference={{
                                  index: index,
                                  field: "amount",
                                  isOpeningBalance: !row.permitToDelete,
                                }}
                                fullWidth={true}
                                disabled={row.status === "autoload"}
                                canEdit={canEditSchedule}
                              />
                            </Box>
                          </Tooltip>
                        ) : (
                          <TextFieldForAmount
                            value={row.amount}
                            onChange={handleInputChange}
                            reference={{
                              index: index,
                              field: "amount",
                              isOpeningBalance: !row.permitToDelete,
                            }}
                            fullWidth={true}
                            disabled={row.status === "autoload"}
                            canEdit={canEditSchedule}
                          />
                        )}
                      </>
                    ),
                  },
                  {
                    key: "balance",
                    label: "Balance",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, _) => (
                      <TextFieldForViewOnly
                        value={
                          row.permitToDelete
                            ? formatAmount(row.balance || 0)
                            : 0
                        }
                      />
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <>
                        {row.permitToDelete ? (
                          <TextFieldForName
                            value={row?.reference || ""}
                            onChange={handleInputChange}
                            reference={{
                              index: index,
                              field: "reference",
                              isOpeningBalance: !row.permitToDelete,
                            }}
                            canEdit={canEditSchedule}
                            viewOnlyAlign='center'
                          />
                        ) : (
                          <></>
                        )}
                      </>
                    ),
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    colAlign: "center",
                    fixedSize: true,
                    render: (row) => (
                      <TextFieldForViewOnly value={row.source?.sch_no || ""} />
                    ),
                  },
                  canEditSchedule
                    ? {
                        key: "action",
                        label: "Action",
                        headerAlign: "center",
                        colAlign: "center",
                        fixedSize: true,
                        render: (row, index) => (
                          <>
                            {row.permitToDelete ? (
                              <IconButtonWithTooltip
                                icon={DeleteOutlineIcon}
                                tooltipTitle='Delete Row'
                                onClick={() => removeRow(index)}
                                color='error'
                                size='small'
                              />
                            ) : (
                              <></>
                            )}
                          </>
                        ),
                      }
                    : [],
                ]}
              >
                <TableCell></TableCell>
                <TableCell align='center' sx={{ fontWeight: "bold" }}>
                  Closing Balance
                </TableCell>
                <TableCell align='right'>
                  {formatAmount(movementBalance)}
                </TableCell>
                <TableCell align='right'>
                  {formatAmount(closingBalance)}
                </TableCell>
                <TableCell colSpan={3}></TableCell>
              </CommonTable>
            </SectionBox>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: "bold" }}>
                Closing Balance
              </Typography>

              <Typography
                variant='h6'
                sx={{
                  fontWeight: "bold",
                }}
              >
                {formatAmount(closingBalance)}
              </Typography>
            </Paper>
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default MovementSchedule;
